import { defineStore } from 'pinia';
import { useNuxtApp } from '#app'; // Needed for Nuxt integration
import { ref } from 'vue';
import { useUserStore } from '~/stores/user';

export const useFileTreeStore = defineStore('file_tree', {
  state: () => ({
    copied: '', // Ensure this is initialized correctly
    copiedItem: null,
    itemOnAction: null,
    structure: [], // Store the flat structure
    treeStructure: {}, // Store the transformed tree structure as an object
    commonStructure: {}, // Store common tree data
    commonTreeStructure: [], // Store common tree data
    activatedItem: {},
    hoveredItem: {},
    loading: false,
    hasFetchedStructure: false,
    _fetchPromise: null,
    treeNodeMap: new Map(), // not undefined
  }),
  actions: {
    async fetchStructureFromDB() {
      if (this._fetchPromise) return this._fetchPromise;

      this._fetchPromise = (async () => {
        this.loading = true;
        const { $supabase } = useNuxtApp();
        const userStore = useUserStore();
        const userId = userStore.user.id;

        try {
          // Fetch collaborators and tree items in parallel
          const [collaboratorsResult, treeResult] = await Promise.all([
            userId && userId !== ''
              ? $supabase
                  .rpc('get_user_collaborators', { p_user_id: userId })
                  .limit(50000)
              : Promise.resolve({ data: [], error: null }),
            $supabase.rpc('get_user_tree', {
              p_user_id: userId && userId !== '' ? userId : null,
            }),
          ]);
          if (collaboratorsResult.error) throw collaboratorsResult.error;
          if (treeResult.error) throw treeResult.error;

          const collaboratorItems = collaboratorsResult.data || [];
          const roleMap = new Map(
            collaboratorItems.map((c) => [c.item_id, c.role])
          );

          const uniqueItems = (treeResult.data || []).map((item) => ({
            ...item,
            collaborator_role: roleMap.get(item.id) ?? null,
          }));

          this.structure = uniqueItems;
          this.commonStructure = uniqueItems.filter(
            (item) => item.user_id === '0'
          );

          const { root: commonRoot } = transformToTree(this.commonStructure);
          this.commonTreeStructure = commonRoot;

          const { root, nodeMap } = transformToTree(this.structure);
          this.treeStructure = root;
          this.treeNodeMap = nodeMap;
          this.hasFetchedStructure = true;
        } catch (err) {
          console.error('Error fetching structure:', err);
        } finally {
          this.loading = false;
          this._fetchPromise = null;
        }
      })();

      return this._fetchPromise;
    },

    async updateProjectAndChildren(projectId, updates) {
      this.loading = true;
      try {
        const { $supabase } = useNuxtApp(); // Access supabase client

        const { name, project_type, organization_id, card_status } = updates;
        const timestamp = new Date().toISOString();
        const is_public = card_status === 'public';

        // 1. Update the project itself in tree_items
        const { error: projectError } = await $supabase
          .from('tree_items')
          .update({
            name: name?.trim(),
            project_type,
            organization_id,
            card_status,
            updated_at: timestamp,
          })
          .eq('id', projectId)
          .eq('is_draft', false)
          .neq('card_status', 'temp');

        if (projectError) {
          this.loading = false;
        }
        // update items local state
        this.updateItemLocalState(projectId, {
          name: name?.trim(),
          project_type,
          organization_id,
          card_status,
          updated_at: timestamp,
        });

        // 2. Update all child items (cards and folders) in tree_items
        const { data: childItemsData, error: childItemsError } = await $supabase
          .from('tree_items')
          .update({
            organization_id,
            card_status,
            updated_at: timestamp,
          })
          .eq('is_draft', false)
          .neq('card_status', 'temp')
          .eq('project_id', projectId)
          .neq('id', projectId)
          .select('*');

        if (childItemsError) this.loading = false;
        // update items local state
        for (const item of childItemsData) {
          this.updateItemLocalState(item.id, {
            organization_id,
            card_status,
            updated_at: timestamp,
          });
        }

        // 3. Update all cards in the cards table
        const { error: cardsError } = await $supabase
          .from('cards')
          .update({
            organization_id,
            is_public,
            last_updated: timestamp,
          })
          .eq('project_id', projectId);

        if (cardsError) this.loading = false;

        this.loading = false;
        return { success: true };
      } catch (error) {
        console.error('Error updating project and children:', error);
        this.loading = false;
        return { success: false, error };
      }
    },
    activateFolder(folder, isRecursive = false) {
      // Expand current folder if not already expanded
      if (!folder) return;
      if (!folder?.expanded) {
        this.updateItemLocalState(folder.id, { expanded: true });
      }

      // Expand parents - use flat structure lookup for efficiency
      if (folder.parent_id) {
        const parent = this.structure.find(
          (item) => item.id === folder.parent_id
        );
        if (parent && parent.parent_id) {
          if (!parent.expanded) {
            this.activateFolder(parent, true);
          }
        }
      }

      if (!isRecursive) {
        this.activatedItem = folder;
      }
    },
    activateCard(card) {
      this.activatedItem = card;
    },
    resetActiveFolders() {
      this.activatedItem = {};
    },
    async reset() {
      this.loading = true;
      this.copied = '';
      this.copiedItem = null;
      this.itemOnAction = null;
      this.structure = this.commonStructure; // Reset to common structure
      this.treeStructure = this.commonTreeStructure; // Reset to common tree structure
      this.activatedItem = {};
      this.hoveredItem = {};
      this.loading = false;
      this.hasFetchedStructure = false;
      this._fetchPromise = null;
      this.treeNodeMap = new Map();
    },

    async getCopiedItem() {
      this.loading = true;
      this.loading = false;
      return this.copiedItem;
    },

    async getfileStructureTree() {
      this.loading = true;
      this.loading = false;
      return this.treeStructure;
    },
    async addItem(item) {
      this.loading = true;

      // Add to flat structure
      const itemWithoutChildren = { ...item };
      delete itemWithoutChildren.children;
      this.structure.push(itemWithoutChildren);

      // Rebuild tree structure and nodeMap since we're adding new items
      const { root, nodeMap } = transformToTree(this.structure);
      this.treeStructure = root;
      this.treeNodeMap = nodeMap;

      this.loading = false;
    },
    async removeItem(item) {
      const { $supabase } = useNuxtApp();

      // Collect every ID to remove up front (before we mutate structure)
      const idsToRemove = collectDescendantIds(item.id, this.structure);

      // Fire all DB deletes in parallel
      await Promise.all(
        idsToRemove.map((id) => {
          const node =
            this.treeNodeMap?.get(id) ??
            this.structure.find((i) => i.id === id);
          const table = node?.type === 'card' ? 'cards' : 'projects';
          return $supabase.from(table).delete().eq('id', id);
        })
      );

      // Update flat structure (one pass, no children field cleanup needed)
      const removeSet = new Set(idsToRemove);
      this.structure = this.structure.filter((i) => !removeSet.has(i.id));

      // Patch the tree in place — no rebuild
      removeNodeFromTree(this.treeNodeMap, item.id);
      // Children are now orphaned in the map; clean them up too
      idsToRemove.slice(1).forEach((id) => this.treeNodeMap?.delete(id));
    },
    async moveDiagramToRecycleBin(diagram) {
      const { $supabase } = useNuxtApp();
      const { error } = await $supabase
        .from('diagrams')
        .update({ in_recycle_bin: true })
        .eq('id', diagram.id);

      if (error) {
        console.error('Error deleting item:', error);
        throw error; // Stop further execution
      }
    },
    async moveToRecycleBin(userID, item) {
      const { $supabase } = useNuxtApp();
      const rootItemID = item.id;

      const idsToRemove = collectDescendantIds(rootItemID, this.structure);

      const recycleBinRows = idsToRemove.map((id) => ({
        item_id: id,
        type: (
          this.treeNodeMap?.get(id) ?? this.structure.find((i) => i.id === id)
        )?.type,
        user_id: userID,
        is_root: id === rootItemID,
        root_id: id === rootItemID ? null : `${rootItemID}`,
      }));

      const cardIds = idsToRemove.filter((id) => {
        const n =
          this.treeNodeMap?.get(id) ?? this.structure.find((i) => i.id === id);
        return n?.type === 'card';
      });

      // Chunk both ID lists
      const treeChunks = chunkArray(idsToRemove, 100);
      const cardChunks = chunkArray(cardIds, 100);

      const [treeResults, cardResults, { error: rbErr }] = await Promise.all([
        Promise.all(
          treeChunks.map((chunk) =>
            $supabase
              .from('tree_items')
              .update({ in_recycle_bin: true })
              .in('id', chunk)
          )
        ),
        Promise.all(
          cardChunks.map((chunk) =>
            $supabase
              .from('cards')
              .update({ in_recycle_bin: true })
              .in('id', chunk)
          )
        ),
        $supabase.from('recycle_bin').upsert(recycleBinRows, {
          onConflict: 'item_id',
          ignoreDuplicates: false,
        }),
      ]);

      const treeErr = treeResults.find((r) => r.error)?.error;
      const cardErr = cardResults.find((r) => r.error)?.error;

      if (treeErr) throw treeErr;
      if (cardErr) throw cardErr;
      if (rbErr) throw rbErr;

      const removeSet = new Set(idsToRemove);
      this.structure = this.structure.filter((i) => !removeSet.has(i.id));

      removeNodeFromTree(this.treeNodeMap, rootItemID);
      idsToRemove.slice(1).forEach((id) => this.treeNodeMap?.delete(id));
    },
    async permDeleteFromRecycleBin(confirmationItem) {
      this.loading = true;
      const { $supabase } = useNuxtApp();

      const { error } = await $supabase
        .from('tree_items')
        .delete()
        .eq('id', confirmationItem.id);
      if (error) {
        console.error('Error deleting item:', error);
        throw error; // Stop further execution
      }
      // remove the item from recycle bin
      const { recyError } = await $supabase
        .from('recycle_bin')
        .delete()
        .eq('item_id', confirmationItem.id);

      if (recyError) {
        console.error('Error deleting item:', recyError);
        throw recyError; // Stop further execution
      }
      const { error: cardError } = await $supabase
        .from('cards')
        .delete()
        .eq('id', confirmationItem.id);

      if (cardError) {
        console.error('Error deleting item:', cardError);
        throw cardError; // Stop further execution
      }

      this.loading = false;
    },
    async getUserPrivateProjectsCount(userId) {
      const { $supabase } = useNuxtApp();
      const { count, error } = await $supabase
        .from('tree_items')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('type', 'project')
        .neq('card_status', 'public')
        .eq('is_draft', false)
        .neq('card_status', 'temp')
        .eq('in_recycle_bin', false);

      if (error) {
        console.error('Error fetching private projects count:', error);
        return 0;
      }
      return count ?? 0;
    },
    async restoreFromRecycleBin(item) {
      this.loading = true;
      const { $supabase } = useNuxtApp();

      try {
        // ── Step 1: Walk ancestor chain upward, collecting all in-recycle-bin ancestors ──
        const ancestorsToShell = []; // ordered bottom-up, we'll reverse later
        let currentParentId = item.parent_id;

        while (currentParentId) {
          // Is this parent currently in the live tree?
          const existsInTree = this.structure.some(
            (s) => s.id === currentParentId
          );
          if (existsInTree) break; // ancestor is live, chain is intact from here up

          // Check if it's in the recycle bin
          const { data: rbRow, error: rbErr } = await $supabase
            .from('recycle_bin')
            .select('*')
            .eq('item_id', currentParentId)
            .maybeSingle();

          if (rbErr) throw rbErr;

          if (!rbRow) {
            // Not in recycle bin and not in tree — orphaned, reroute the item instead
            break;
          }

          // Fetch the actual tree_item data for this ancestor
          const { data: ancestorData, error: ancestorErr } = await $supabase
            .from('tree_items')
            .select('*')
            .eq('id', currentParentId)
            .single();

          if (ancestorErr) throw ancestorErr;

          ancestorsToShell.push({ treeItem: ancestorData, rbRow });
          currentParentId = ancestorData.parent_id;
        }

        // ancestorsToShell is bottom-up — reverse to restore top-down
        ancestorsToShell.reverse();

        // ── Step 2: Shell-restore each ancestor top-down ──
        for (const { treeItem, rbRow } of ancestorsToShell) {
          // Only shell-restore if not already live
          const alreadyLive = this.structure.some((s) => s.id === treeItem.id);
          if (alreadyLive) continue;

          // Check if THIS ancestor's parent needs rerouting
          const parentIsLive =
            !treeItem.parent_id ||
            this.structure.some((s) => s.id === treeItem.parent_id);

          let shellParentId = treeItem.parent_id;
          if (!parentIsLive) {
            // Parent was already handled in the loop above and added to structure
            // (because we restore top-down, parent is always done before child)
            // If somehow still not live, reroute to my-projects
            const myProjectsFolder = await this.getMyProjectsFolder();
            shellParentId = myProjectsFolder.id;
          }

          // Restore to tree (make visible) but keep recycle_bin row with is_shell = true
          const { data: restoredAncestor, error: restoreErr } = await $supabase
            .from('tree_items')
            .update({ in_recycle_bin: false, parent_id: shellParentId })
            .eq('id', treeItem.id)
            .select('*')
            .single();

          if (restoreErr) throw restoreErr;

          // Mark recycle_bin row as shell (still visible in recycle bin)
          const { error: shellErr } = await $supabase
            .from('recycle_bin')
            .update({ is_shell: true })
            .eq('item_id', treeItem.id);

          if (shellErr) throw shellErr;

          // Add to local structure so subsequent ancestors can find their parent
          if (!this.structure.some((s) => s.id === restoredAncestor.id)) {
            this.structure.push({
              ...restoredAncestor,
              collaborator_role: 'owner',
            });
          }
        }

        // ── Step 3: Determine final parent for the item itself ──
        const parentIsLiveNow =
          !item.parent_id ||
          this.structure.some((s) => s.id === item.parent_id);

        let overrideParentId = null;
        if (!parentIsLiveNow) {
          // Ancestor chain was broken (orphaned) — reroute
          if (item.type === 'card') {
            const freeCardsFolder = await this.getFreeCardsFolder();
            if (!freeCardsFolder)
              throw new Error('free-cards folder not found');
            overrideParentId = freeCardsFolder.id;
          } else {
            const myProjectsFolder = await this.getMyProjectsFolder();
            if (!myProjectsFolder)
              throw new Error('my-projects folder not found');
            overrideParentId = myProjectsFolder.id;
          }
        }

        // ── Step 4: Fully restore the target item ──
        const restoreItem = async (itemToRestore, overrideParent = null) => {
          if (itemToRestore.type === 'card') {
            const { error } = await $supabase
              .from('cards')
              .update({ in_recycle_bin: false })
              .eq('id', itemToRestore.id);
            if (error) throw error;
          }

          const treeUpdates = { in_recycle_bin: false };
          if (overrideParent !== null) treeUpdates.parent_id = overrideParent;

          const { data: fetchedData, error: treeError } = await $supabase
            .from('tree_items')
            .update(treeUpdates)
            .eq('id', itemToRestore.id)
            .select('*')
            .single();
          if (treeError) throw treeError;

          const { error: deleteError } = await $supabase
            .from('recycle_bin')
            .delete()
            .eq('item_id', itemToRestore.id);
          if (deleteError) throw deleteError;

          if (!this.structure.some((s) => s.id === fetchedData.id)) {
            this.structure.push({
              ...fetchedData,
              collaborator_role: 'owner',
            });
          }
        };

        await restoreItem(item, overrideParentId);

        // ── Step 5: Restore children of the item (their parent is the item itself, always safe) ──
        const { data: children, error: childErr } = await $supabase
          .from('recycle_bin')
          .select('*')
          .eq('root_id', item.id);
        if (childErr) throw childErr;

        for (const child of children) {
          const { data: childData, error: fetchErr } = await $supabase
            .from('tree_items')
            .select('*')
            .eq('id', child.item_id)
            .single();
          if (fetchErr) throw fetchErr;
          await restoreItem(childData);
        }
      } catch (error) {
        console.error('Error during restore:', error);
      } finally {
        const { root, nodeMap } = transformToTree(this.structure);
        this.treeStructure = root;
        this.treeNodeMap = nodeMap;
        this.loading = false;
      }
    },

    updateItemLocalState(itemId, updates) {
      const item = this.structure.find((item) => item.id === itemId);

      if (!item) {
        console.error(`Item with ID ${itemId} not found.`);
        return;
      }

      // Apply updates to flat structure
      Object.assign(item, updates);

      // Also update the corresponding node in treeStructure using nodeMap
      if (this.treeNodeMap && this.treeNodeMap.has(itemId)) {
        const treeNode = this.treeNodeMap.get(itemId);
        Object.assign(treeNode, updates);
      }

      // Trigger reactivity
      this.$patch((state) => {
        state.structure = state.structure;
      });
    },
    async updateItem(itemId, updates) {
      const itemIndex = this.structure.findIndex((item) => item.id === itemId);

      if (itemIndex !== -1) {
        // Check if updates affect tree structure (parent_id changes)
        const needsTreeRebuild = updates.hasOwnProperty('parent_id');

        if (needsTreeRebuild) {
          // Update flat structure
          this.structure[itemIndex] = {
            ...this.structure[itemIndex],
            ...updates,
          };
          this.structure = [...this.structure];

          // Rebuild tree structure for structural changes
          const { root, nodeMap } = transformToTree(this.structure);
          this.treeStructure = root;
          this.treeNodeMap = nodeMap;
        } else {
          // Use local update for non-structural changes
          this.updateItemLocalState(itemId, updates);
        }

        // Update database
        const { $supabase } = useNuxtApp();
        const { error } = await $supabase
          .from('tree_items')
          .update(updates)
          .eq('id', itemId)
          .select('*');

        if (error) {
          console.error('Error updating item in database:', error);
        }
      } else {
        console.error(`Item with ID ${itemId} not found.`);
      }
    },
    async createProject(
      projectID,
      projectName,
      organizationId = null,
      projectType = 'generic',
      parent = null,
      isProjectPublic = false
    ) {
      const userStore = useUserStore();
      const { $supabase } = useNuxtApp(); // Access supabase client
      const myProjectsFolder = await this.getMyProjectsFolder();
      this.parent = myProjectsFolder;
      const newProject = {
        id: projectID,
        name: projectName,
        user_id: userStore.user.id,
        project_type: projectType,
        project_id: projectID,
        type: 'project',
        organization_id: organizationId || null, // Use the provided organization ID or null
        parent_id: myProjectsFolder.id,
        card_status: isProjectPublic ? 'public' : 'private',
      };
      const { data, error } = await $supabase
        .from('tree_items')
        .insert(newProject)
        .select('*')
        .maybeSingle();

      const project = data;
      project.collaborator_role = 'owner';
      this.addItem(project);
      if (error) {
        console.error('Error adding project:', error);
      }
      return project;
    },
    async createFolder(
      folderID,
      folderName,
      organizationId = null,
      projectType = 'generic',
      parent = null,
      isDraft = false
    ) {
      const userStore = useUserStore();
      const { $supabase } = useNuxtApp(); // Access supabase client
      const myProjectsFolder = await this.getMyProjectsFolder();
      const newFolder = {
        id: folderID,
        name: folderName,
        user_id: userStore.user.id,
        project_type: projectType,
        type: 'folder',
        card_status: parent?.card_status || 'private', // Inherit from parent or default to private
        is_draft: isDraft,
        organization_id: organizationId || null, // Use the provided organization ID or null
        parent_id: parent?.id || myProjectsFolder.id, // Default to "My Projects" folder if no parent id provided
        project_id: parent?.project_id || null,
      };
      this.parent = parent || myProjectsFolder;
      const { data, error } = await $supabase
        .from('tree_items')
        .insert(newFolder)
        .select('*')
        .maybeSingle();

      const folder = data;
      folder.collaborator_role = 'owner';
      this.addItem(folder);
      if (error) {
        console.error('Error adding folder:', error);
      }
      return folder;
    },

    async createCard(
      cardID,
      cardName,
      folder,
      folderType,
      organizationId = null,
      cardStatus,
      is_draft = false
    ) {
      const userStore = useUserStore();
      const { $supabase } = useNuxtApp();
      const newCard = {
        id: cardID,
        name: cardName,
        parent_id: folder.id,
        user_id: userStore.user.id,
        project_type: folderType,
        card_status: cardStatus || folder?.card_status,
        is_draft: is_draft,
        type: 'card',
        organization_id: organizationId || null,
        project_id: folder.project_id || null,
      };
      const { data, error } = await $supabase
        .from('tree_items')
        .insert(newCard)
        .select('*');

      const card = data[0];
      card.collaborator_role = 'owner';
      this.addItem(card);

      if (error) {
        console.error('Error adding card:', error);
      }
    },
    async getFreeCardsFolder() {
      const { $supabase } = useNuxtApp();
      const { data, error } = await $supabase
        .from('tree_items')
        .select('*')
        .eq('type', 'free-cards')
        .eq('user_id', '0')
        .single();
      if (error || !data) {
        console.error('Error finding free-cards folder:', error);
        return null;
      }
      return data;
    },
    async getMyProjectsFolder() {
      const { $supabase } = useNuxtApp();
      const { data, error } = await $supabase
        .from('tree_items')
        .select('*')
        .eq('type', 'my-projects')
        .eq('user_id', '0')
        .single();
      if (error || !data) {
        console.error('Error finding my-projects folder:', error);
        return null;
      }
      return data;
    },
    async getMySpaceFolder() {
      const { $supabase } = useNuxtApp();
      const { data, error } = await $supabase
        .from('tree_items')
        .select('*')
        .eq('type', 'root')
        .single();
      if (error || !data) {
        console.error('Error finding my-space folder:', error);
        return null;
      }
      return data;
    },

    async ensureCard(
      cardID,
      title,
      cardStatus = 'temp',
      is_draft = false,
      parent,
      projectType = 'generic',
      organizationId = null
    ) {
      // Use existing findItem function to check if card exists

      let finalParent = parent;
      const item = await this.findItem(cardID);
      const freeCardsFolder = await this.getFreeCardsFolder();
      if (
        parent == null ||
        parent.id === 'null' ||
        parent.id === null ||
        parent.id === undefined
      ) {
        finalParent = freeCardsFolder;
      }

      if (!item) {
        // Use existing createCard function
        await this.createCard(
          cardID,
          title,
          finalParent,
          projectType,
          organizationId === 'null' || organizationId === null
            ? null
            : organizationId,
          cardStatus,
          is_draft
        );
      } else {
        // Use existing updateItem function
        await this.updateItem(cardID, {
          name: title,
          card_status: cardStatus,
          updated_at: new Date().toISOString(),
          parent_id: finalParent.id || null,
          project_id: finalParent.project_id || null,
          organization_id:
            organizationId === 'null' || organizationId === null
              ? null
              : organizationId,
          project_type: projectType,
          is_draft: is_draft,
        });
      }
    },
    async toggleExpand(itemId) {
      this.loading = true;
      const item = this.findItemSync(itemId);
      if (item) {
        const newExpandedState = !item.expanded;

        // Update both flat structure and tree node
        this.updateItemLocalState(itemId, { expanded: newExpandedState });

        // Update database
        const { $supabase } = useNuxtApp();
        const { error } = await $supabase
          .from('tree_items')
          .update({ expanded: newExpandedState })
          .eq('id', itemId);

        if (error) {
          console.error('Error updating item in database:', error);
          // Rollback on error
          this.updateItemLocalState(itemId, { expanded: !newExpandedState });
        }
      } else {
        console.error(`Item with ID ${itemId} not found.`);
      }
      this.loading = false;
    },
    findItemSync(itemId) {
      // Use nodeMap for direct lookup if available
      if (this.treeNodeMap && this.treeNodeMap.has(itemId)) {
        return this.treeNodeMap.get(itemId);
      }

      // Fallback to flat structure search
      return this.structure.find((item) => item.id === itemId);
    },
    toggleExpandSync(itemId) {
      this.loading = true;
      const item = this.findItemSync(itemId);
      if (item) {
        const newExpandedState = !item.expanded;
        this.updateItemLocalState(itemId, { expanded: newExpandedState });
      } else {
        console.error(`Item with ID ${itemId} not found.`);
      }
      this.loading = false;
    },
    async getMyProjectsFolderId() {
      const { $supabase } = useNuxtApp();
      const { data, error } = await $supabase
        .from('tree_items')
        .select('id')
        .eq('type', 'my-projects')
        .eq('user_id', '0')
        .single();

      if (error || !data) {
        console.error('Error finding my-projects folder:', error);
        return null;
      }
      return data.id;
    },
    async getMySpaceFolderId() {
      const { $supabase } = useNuxtApp();
      const { data, error } = await $supabase
        .from('tree_items')
        .select('id')
        .eq('type', 'root')
        .single();

      if (error || !data) {
        console.error('Error finding my-space folder:', error);
        return null;
      }
      return data.id;
    },

    async findItem(itemId) {
      try {
        // Use nodeMap for direct lookup if available
        if (this.treeNodeMap && this.treeNodeMap.has(itemId)) {
          const found = this.treeNodeMap.get(itemId);
          return {
            ...found,
            children: found.children || [],
          };
        }

        // Fallback to flat structure search if nodeMap not available or item not found
        const item = this.structure.find((item) => item.id === itemId);
        if (item) {
          // Get children from flat structure
          const children = this.structure.filter(
            (child) => child.parent_id === itemId
          );
          return {
            ...item,
            children: children,
          };
        }

        return null;
      } catch (error) {
        console.error('Error finding item:', error);
        return null;
      }
    },
  },
});

// Helper to find a node in your tree
const findNodeInTree = (tree, id) => {
  for (const node of tree) {
    if (node.id === id) return node;
    if (node.children) {
      const found = findNodeInTree(node.children, id);
      if (found) return found;
    }
  }
  return null;
};

// Function to transform flat structure into a tree object
const transformToTree = (flatData) => {
  const nodeMap = new Map();

  // Step 1: map IDs to node objects
  flatData.forEach((item) => {
    nodeMap.set(item.id, { ...item, children: [] });
  });

  let root = null;

  // Step 2: assign children / find root
  flatData.forEach((item) => {
    if (item.parent_id) {
      const parent = nodeMap.get(item.parent_id);
      if (parent) {
        parent.children.push(nodeMap.get(item.id));
      }
    } else {
      if (item.type === 'root' && item.user_id === '0') {
        root = nodeMap.get(item.id);
      }
    }
  });

  // Step 3: sorting
  const typePriority = {
    'my-projects': 1,
    'free-cards': 2,
    folder: 3,
    card: 4,
  };

  const sortChildren = (node) => {
    if (!node.children.length) return;
    node.children.sort((a, b) => {
      const pa = typePriority[a.type] ?? Infinity;
      const pb = typePriority[b.type] ?? Infinity;
      if (pa !== pb) return pa - pb;
      return new Date(a.created_at) - new Date(b.created_at);
    });
    node.children.forEach(sortChildren);
  };

  if (root) sortChildren(root);
  return { root, nodeMap };
};
const removeNodeFromTree = (nodeMap, itemId) => {
  const node = nodeMap.get(itemId);
  if (!node) return;

  if (node.parent_id) {
    const parent = nodeMap.get(node.parent_id);
    if (parent?.children) {
      const idx = parent.children.findIndex((c) => c.id === itemId);
      if (idx !== -1) parent.children.splice(idx, 1);
    }
  }

  nodeMap.delete(itemId);
};

// Collects an item's ID + all descendant IDs from the flat structure.
const collectDescendantIds = (itemId, structure) => {
  const ids = [itemId];
  const children = structure.filter((i) => i.parent_id === itemId);
  for (const child of children) {
    ids.push(...collectDescendantIds(child.id, structure));
  }
  return ids;
};

const chunkArray = (arr, size) => {
  const chunks = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
};
